import { caixaService } from "../../services/apiService";

export const INTERVALO_PEDIDO_MS = 2000;
// Validade do pedido (2 min) + execução máxima do robô (30 min) + folga.
export const PRAZO_ACOMPANHAMENTO_MS = 35 * 60 * 1000;
export const LIMITE_DIAS_POR_INTEGRACAO = 31;
const FALHAS_SEGUIDAS_PERMITIDAS = 3;
// Cada requisição tem prazo próprio: uma que nunca responde não pode segurar o laço
// (e o modal) além do prazo total.
export const PRAZO_CONSULTA_MS = 15000;
export const PRAZO_CRIACAO_PEDIDO_MS = 30000;
export const MENSAGEM_PRAZO_ESGOTADO =
  "A integração demorou mais que o esperado. Confira o robô e tente novamente.";

export const STATUS_FINAIS = [
  "CONCLUIDO",
  "EXPIRADO",
  "ERRO",
  "SESSAO_EXPIRADA",
  "ERRO_INTEGRACAO",
  "DESCARTADO",
];

export const ETAPAS = {
  PENDENTE: "Aguardando o robô…",
  EM_EXECUCAO: "Executando no Sisodonto…",
  EXTRAIDO: "Criando caixas…",
  INTEGRANDO: "Criando caixas…",
};

export const MENSAGEM_ROBO_OFFLINE =
  "O robô está offline. Abra o robô no seu PC e tente novamente.";

// Lançada quando a tela é fechada: o acompanhamento para sem relatar erro.
export class AcompanhamentoCancelado extends Error {}

// Uma requisição não respondeu dentro do prazo dela.
export class PrazoEsgotado extends Error {}

/**
 * Devolve a promessa, ou rejeita com PrazoEsgotado se ela não resolver em
 * `milissegundos`. A requisição original não é abortada (o fetch do apiService não
 * aceita sinal), mas deixa de segurar quem a espera; se responder depois, é ignorada.
 */
export const comPrazo = (promessa, milissegundos, mensagem) =>
  new Promise((resolve, reject) => {
    const temporizador = setTimeout(
      () => reject(new PrazoEsgotado(mensagem)),
      milissegundos,
    );
    promessa.then(
      (valor) => {
        clearTimeout(temporizador);
        resolve(valor);
      },
      (erro) => {
        clearTimeout(temporizador);
        reject(erro);
      },
    );
  });

const esperar = (milissegundos) =>
  new Promise((resolve) => setTimeout(resolve, milissegundos));

/**
 * Consulta o pedido a cada 2 s até um estado final e devolve o pedido final.
 * `cancelado()` indica que a tela saiu; `aoAtualizar` recebe cada consulta.
 * Falhas isoladas de rede são toleradas (3 seguidas encerram) e há um prazo
 * total para o caso de o robô travar sem avisar.
 */
export const aguardarPedidoFinal = async (
  pedidoInicial,
  { cancelado, aoAtualizar },
) => {
  const inicio = Date.now();
  let falhasSeguidas = 0;

  while (true) {
    if (cancelado()) {
      throw new AcompanhamentoCancelado();
    }

    // O prazo total vale ANTES de cada requisição e limita o tempo dela: se a requisição
    // nunca responder, o laço termina no prazo, e não depois que ela voltar (se voltar).
    const restante = PRAZO_ACOMPANHAMENTO_MS - (Date.now() - inicio);
    if (restante <= 0) {
      throw new Error(MENSAGEM_PRAZO_ESGOTADO);
    }

    try {
      const atual = await comPrazo(
        caixaService.obterPedidoSisodonto(pedidoInicial.id),
        Math.min(PRAZO_CONSULTA_MS, restante),
        "O servidor não respondeu a tempo ao acompanhar a integração.",
      );
      if (cancelado()) {
        throw new AcompanhamentoCancelado();
      }
      falhasSeguidas = 0;
      aoAtualizar?.(atual);
      if (STATUS_FINAIS.includes(atual.status)) {
        return atual;
      }
    } catch (erro) {
      if (erro instanceof AcompanhamentoCancelado || cancelado()) {
        throw new AcompanhamentoCancelado();
      }
      falhasSeguidas += 1;
      if (erro.status === 404 || falhasSeguidas >= FALHAS_SEGUIDAS_PERMITIDAS) {
        throw erro;
      }
    }

    if (Date.now() - inicio >= PRAZO_ACOMPANHAMENTO_MS) {
      throw new Error(MENSAGEM_PRAZO_ESGOTADO);
    }
    await esperar(INTERVALO_PEDIDO_MS);
  }
};

const separarData = (dataIso) => {
  const [ano, mes, dia] = dataIso.split("-").map(Number);
  return new Date(Date.UTC(ano, mes - 1, dia));
};

/** Datas (yyyy-MM-dd) de inicial a final, inclusive. Sem fuso: usa UTC. */
export const diasDoPeriodo = (dataInicial, dataFinal) => {
  const dias = [];
  const fim = separarData(dataFinal);
  for (
    let atual = separarData(dataInicial);
    atual <= fim;
    atual = new Date(atual.getTime() + 24 * 60 * 60 * 1000)
  ) {
    dias.push(atual.toISOString().slice(0, 10));
  }
  return dias;
};

export const formatarDataBr = (dataIso) => {
  const [ano, mes, dia] = dataIso.split("-");
  return `${dia}/${mes}/${ano}`;
};

export const hojeLocal = () => new Date().toLocaleDateString("en-CA");

/** Mensagem de erro do período escolhido; texto vazio quando está válido. */
export const validarPeriodo = (dataInicial, dataFinal, hoje = hojeLocal()) => {
  if (!dataInicial || !dataFinal) {
    return "Informe a data inicial e a data final.";
  }
  if (dataFinal < dataInicial) {
    return "A data final não pode ser anterior à data inicial.";
  }
  if (dataFinal > hoje) {
    return "A data final não pode ser futura.";
  }
  if (
    diasDoPeriodo(dataInicial, dataFinal).length > LIMITE_DIAS_POR_INTEGRACAO
  ) {
    return `O período máximo é de ${LIMITE_DIAS_POR_INTEGRACAO} dias.`;
  }
  return "";
};

export const mensagemDeFalhaDoPedido = (pedido) => {
  switch (pedido.status) {
    case "EXPIRADO":
      return "O robô não respondeu a tempo; tente novamente.";
    case "DESCARTADO":
      return "O resultado não foi integrado a tempo; tente novamente.";
    default:
      return pedido.mensagemErro || "A integração com o Sisodonto falhou.";
  }
};
